import { api } from '../../lib/api';
import type { AppId } from '../../lib/appConstants';
export type CampaignEnvironment = 'SANDBOX' | 'PRODUCTION';
export interface CampaignCostBasis {
  terms: {
    tier: 'premium' | 'premium_plus';
    discountMinor: number | null;
    renewalMinor: number | null;
  };
  netReceiptLowerMinor: number | null;
  variableCostUpperMinor: number | null;
  minimumContributionMinor: number | null;
  source: string;
  asOf: string | null;
  validUntil: string | null;
}
export type CampaignEconomicsInput = Partial<Record<'APP_STORE' | 'PLAY_STORE', CampaignCostBasis>>;
export interface CampaignEconomicsReport {
  mode: 'PLANNING_ONLY';
  finance: 'NOT_VERIFIED';
  requiredThrough: string;
  providers: {
    provider: 'APP_STORE' | 'PLAY_STORE';
    status: 'NOT_ASSESSED' | 'CALCULATED';
    reasons: string[];
    monthlyContributionLowerMinor: number | null;
    threePaidMonthsContributionLowerMinor: number | null;
    minimumContributionMinor: number | null;
    thresholdMet: boolean | null;
  }[];
}
export interface CampaignReleaseReview {
  policyVersion: 1;
  status: 'BLOCKED';
  items: { code: string; status: string }[];
  approvalAllowed: false;
  deliveryEnabled: false;
}
export interface CampaignConfiguration {
  economics?: CampaignEconomicsInput;

  name: string;
  template: 'T1' | 'R1' | 'I2' | 'P1' | 'F1';
  tier: 'premium' | 'premium_plus';
  storefront: 'DEU';
  discountMinor: number | null;
  renewalMinor: number | null;
  currency: 'EUR';
  cycles: 3;
  period: 'P1M';
  startsAt: string;
  endsAt: string;
  timeZone: 'Europe/Berlin';
  redemptionWindowDays: number;
  maxAssignments: number;
  maxReservedMinor: number;
  pushRequested: boolean;
  title: string;
  body: string;
}
export interface AudiencePreview {
  observedAt: string;
  completedAt: string;
  oldestFactsAt: string | null;
  complete: boolean;
  rulesHash: string;
  counts: {
    appAccounts: number;
    examined: number;
    sufficientData: number;
    withoutExclusions: number;
    segmentCandidatesBeforeStore: number;
    storeAndBuildEligible: number | null;
    inAppEligible: number | null;
    pushConsentAndInstallation: number | null;
    currentlyContactable: number | null;
  };
  exclusionReasons: { code: string; count: number }[];
  gates: string[];
}
export interface CampaignStorePlan {
  firstPurchase?:{apple:'NEW_ONLY_CODE_BATCH_REQUIRED';google:'DEVELOPER_OFFER';stripe:'SEPARATE_WEB_COUPON';release:'NATIVE_STORE_REVIEW_AND_DEVICE_TEST_REQUIRED'};
  planHash: string;
  sourceVersion: number;
  createdAt: string;
  validUntil: string;
  status: 'PLANNED' | 'BLOCKED';
  blockers: string[];
  roundedDiscountPercent: number | null;
  reservationMinor: number | null;
  margin: 'NOT_VERIFIED';
  providers: {
    provider: 'APP_STORE' | 'PLAY_STORE';
    binding: { productId: string; offerId: string; offeringId?: string };
  }[];
}
export interface CampaignStoreReadback {
  planHash: string;
  observedAt: string;
  validUntil: string;
  status: 'CATALOG_MATCHED' | 'PENDING';
  providers: {
    provider: 'APP_STORE' | 'PLAY_STORE';
    native: 'MATCHED' | 'UNVERIFIED';
    revenueCat: 'MATCHED' | 'UNVERIFIED';
  }[];
}
export interface CampaignJournalReadback {
  planHash: string;
  observedAt: string;
  validUntil: string;
  status: 'JOURNALS_MATCHED' | 'PENDING';
  providers: {
    provider: 'APP_STORE' | 'PLAY_STORE';
    status: 'MATCHED' | 'UNVERIFIED';
    reason: string | null;
  }[];
}
export interface CampaignDeviceEvidence {
  status: 'PARTIAL' | 'NOT_LINKED';
  observation: {
    observedAt: string;
    checks: Record<
      'offerDisplay' | 'restart' | 'cancellation' | 'expiry' | 'priceSequence',
      'TESTER_CONFIRMED' | 'HISTORICALLY_FAILED' | 'PROVIDER_CONFIRMED' | 'NOT_VERIFIED'
    >;
    sources: { document: string; sha256: string; section?: string }[];
  } | null;
  currentBuild: 'NOT_VERIFIED';
  releaseAccepted: false;
}
export interface CampaignReleaseEvidence {
  economics?: CampaignEconomicsReport;
  releaseReview?: CampaignReleaseReview;
  sourceVersion: number;
  observedAt: string;
  validUntil: string;
  status: 'PENDING';
  planHash: string;
  catalog: 'MATCHED' | 'RECHECK_REQUIRED';
  journals: 'MATCHED' | 'RECHECK_REQUIRED';
  purchases: {
    complete: boolean;
    examined: number;
    providers: {
      provider: 'APP_STORE' | 'PLAY_STORE';
      confirmedPurchases: number;
      latestPaidAt: string | null;
      deviceTests?: CampaignDeviceEvidence;
    }[];
  };
  personalization: {
    appAccounts: number;
    allowed: number;
    denied: number;
    unknown: number;
  };
  push: 'NOT_VERIFIED' | 'NOT_REQUESTED';
  budget: {
    reservationMinor: number | null;
    maxAssignments: number;
    maxReservedMinor: number;
    maximumDiscountMinor: number | null;
    fundedAssignments: number;
  };
  approvalAllowed: false;
  deliveryEnabled: false;
}
export interface CampaignDraft {
  id: string;
  app: AppId;
  environment: CampaignEnvironment;
  version: number;
  configuration: CampaignConfiguration;
  state: 'DRAFT';
  preview: AudiencePreview | null;
  previewHash: string | null;
  storePlan: CampaignStorePlan | null;
  storePlanHash: string | null;
  storeReadback: CampaignStoreReadback | null;
  storeJournalReadback?: CampaignJournalReadback | null;
  releaseEvidence?: CampaignReleaseEvidence | null;
  createdAt: string;
  updatedAt: string;
  gates: { provision: false; approve: false; schedule: false; reason: string };
}
export interface CampaignReleaseSnapshot {
  id: string;
  sourceVersion: number;
  manifestHash: string;
  createdAt: string;
  assessment: {
    status: 'REVIEW_ONLY' | 'EXPIRED' | 'SUPERSEDED';
    blockers: string[];
    approvalAllowed: false;
    deliveryEnabled: false;
  };
}
export interface CampaignRuntimeRevision {
  id: string;
  app: AppId;
  environment: CampaignEnvironment;
  provider: string;
  revision: string;
  state: string;
  version: number;
  startsAt: string;
  endsAt: string;
  paidContractsAffected: false;
  push: 'NOT_CONNECTED';
  events: {
    action: string;
    version: number;
    reason: string;
    previousState: string;
    nextState: string;
    createdAt: string;
  }[];
}
export interface CampaignContext {
  appCapabilities?: Partial<Record<AppId, { approve: boolean; pause: boolean; finance?: boolean }>>;
  environment: CampaignEnvironment;
  capabilities: {
    read: boolean;
    edit: boolean;
    preview: boolean;
    provision: boolean;
    approve: boolean;
    pause: boolean;
  };
}
const scope = (app: AppId, environment: CampaignEnvironment) =>
  '?' + new URLSearchParams({ app, environment });
const version = (v: number) => ({ headers: { 'If-Match': `"${v}"` } });
export const campaignContext = () => api.get<CampaignContext>('/admin/campaigns/context');
export const campaignDrafts = (app: AppId | undefined, environment: CampaignEnvironment) =>
  api.get<{ items: CampaignDraft[]; hasMore: boolean }>(
    '/admin/campaigns?' + new URLSearchParams({ ...(app ? { app } : {}), environment }),
  );
export const saveCampaignDraft = (
  app: AppId,
  env: CampaignEnvironment,
  configuration: CampaignConfiguration,
  row?: CampaignDraft,
) =>
  row
    ? api.patch<CampaignDraft>(
        '/admin/campaigns/' + row.id + '/draft' + scope(app, env),
        configuration,
        version(row.version),
      )
    : api.post<CampaignDraft>('/admin/campaigns' + scope(app, env), configuration);
export const previewCampaignDraft = (row: CampaignDraft) =>
  api.post<CampaignDraft>(
    '/admin/campaigns/' + row.id + '/preview' + scope(row.app, row.environment),
    {},
    version(row.version),
  );
export const cloneCampaignDraft = (row: CampaignDraft, targetApp: AppId) =>
  api.post<CampaignDraft>(
    '/admin/campaigns/' + row.id + '/clone' + scope(row.app, row.environment),
    { targetApp },
    version(row.version),
  );

export interface CampaignAuditEvent {
  action: string;
  version: number;
  createdAt: string;
}
export const campaignAudit = (row: CampaignDraft) =>
  api.get<CampaignDraft & { events: CampaignAuditEvent[] }>(
    '/admin/campaigns/' + row.id + scope(row.app, row.environment),
  );

export const planCampaignDraft = (row: CampaignDraft) =>
  api.post<CampaignDraft>(
    '/admin/campaigns/' + row.id + '/store-plan' + scope(row.app, row.environment),
    {},
    version(row.version),
  );
export const readbackCampaignDraft = (row: CampaignDraft) =>
  api.post<CampaignDraft>(
    '/admin/campaigns/' + row.id + '/store-readback' + scope(row.app, row.environment),
    {},
    version(row.version),
  );

export const readbackCampaignJournals = (row: CampaignDraft) =>
  api.post<CampaignDraft>(
    '/admin/campaigns/' + row.id + '/store-journal-readback' + scope(row.app, row.environment),
    {},
    version(row.version),
  );

export const checkCampaignEvidence = (row: CampaignDraft) =>
  api.post<CampaignDraft>(
    '/admin/campaigns/' + row.id + '/evidence-check' + scope(row.app, row.environment),
    {},
    version(row.version),
  );

export const campaignReleaseSnapshots = (row: CampaignDraft) =>
  api.get<{ items: CampaignReleaseSnapshot[]; hasMore: boolean }>(
    '/admin/campaigns/' + row.id + '/release-snapshots' + scope(row.app, row.environment),
  );
export const prepareCampaignRelease = (row: CampaignDraft) =>
  api.post<CampaignReleaseSnapshot>(
    '/admin/campaigns/' + row.id + '/release-snapshots' + scope(row.app, row.environment),
    {},
    version(row.version),
  );
export const campaignRuntimeRevisions = (row: CampaignDraft) =>
  api.get<{ items: CampaignRuntimeRevision[]; hasMore: boolean }>(
    '/admin/campaigns/revisions' + scope(row.app, row.environment),
  );
export const controlCampaignRevision = (
  row: CampaignRuntimeRevision,
  action: 'PAUSE' | 'STOP',
  reason: string,
) =>
  api.post<{ state: string; version: number }>(
    '/admin/campaigns/revisions/' +
      encodeURIComponent(row.id) +
      '/control' +
      scope(row.app, row.environment),
    { action, confirmation: action, reason },
    version(row.version),
  );

export interface CampaignFinanceReview {
  draftVersion: number;
  reviewVersion: number;
  status: 'NOT_REVIEWED' | 'CONFIRMED' | 'WITHDRAWN' | 'SUPERSEDED' | 'EXPIRED_OR_INVALID';
  confirmable: boolean;
  calculation: CampaignEconomicsReport;
  items: {
    id: string;
    version: number;
    draftVersion: number;
    decision: 'CONFIRM' | 'WITHDRAW';
    createdAt: string;
    manifestHash: string;
    manifest: { sourceReference: string; reason: string };
  }[];
  hasMore: boolean;
  approvalAllowed: false;
  deliveryEnabled: false;
}
export const campaignFinanceReviews = (row: CampaignDraft) =>
  api.get<CampaignFinanceReview>(
    '/admin/campaigns/' + row.id + '/finance-reviews' + scope(row.app, row.environment),
  );
export const decideCampaignFinance = (
  row: CampaignDraft,
  decision: 'CONFIRM' | 'WITHDRAW',
  expectedReviewVersion: number,
  sourceReference: string,
  reason: string,
) =>
  api.post<CampaignFinanceReview>(
    '/admin/campaigns/' + row.id + '/finance-reviews' + scope(row.app, row.environment),
    {
      decision,
      confirmation: 'FINANCE_' + decision,
      expectedReviewVersion,
      sourceReference,
      reason,
    },
    version(row.version),
  );
